import anime from "animejs";
import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { spinner } from "~/styles/mixins";
import { smallerThan } from "~/styles/theme";
import partDimensions from "./partDimensions";
import partStates from "./partStates";

const SquareWrapper = styled.div`
  display: block;
  width: ${(p) => p.width || "100%"};
  height: ${(p) => p.height || "100%"};
  position: relative;
  > div {
    width: 100%;
    height: 100%;
    /*
    height: 0;
    padding-bottom: ${(p) => p.height * 100 || 100}%;
    */
    > div {
      overflow: hidden;
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      /* background-color: tomato; */
      ${(p) => smallerThan(p.theme.breakpoints.configuratorSmall)} {
        top: -15px;
      }
    }
  }
`;

const Square = React.forwardRef((props, ref) => {
  const { children, width = 1.0, height = 1.0 } = props;

  // console.log('width', width, typeof width);
  // console.log('height', height, typeof height);

  // width = "100%",  height = 1.2, we scale after width
  // height = "100%", width = 1.2, we scale after width...

  let squareWidth = 0;
  let squareHeight = 0;

  // let rect;
  if (ref.current) {
    const rect = ref.current.getBoundingClientRect();
    if (typeof width === "string" && typeof height === "number") {
      squareWidth = width;
      squareHeight = `${rect.width * height}px`;
    }
    if (typeof width === "number" && typeof height === "string") {
      squareWidth = `${rect.height * width}px`;
      squareHeight = height;
    }
  }
  // console.log('squareWidth', squareWidth);
  // console.log('squareHeight', squareHeight);

  return (
    <SquareWrapper width={squareWidth} height={squareHeight}>
      <div>
        <div ref={ref}>{children}</div>
      </div>
    </SquareWrapper>
  );
});

const percentOfMaster = (value) => (100 / sizes.master) * value;

// a modular part component that gets it's dimensions from the
// `partDimensions` based on the `id` eg. "earpads_front_a"
const ModularPartWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${(p) => p.zIndex};
  > div {
    position: absolute;
    left: ${(p) => p.left};
    top: ${(p) => p.top};
    width: ${(p) => p.width};
    height: ${(p) => p.height};
    background-size: cover;
    background-repeat: no-repeat;
    img {
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      opacity: 0;
      width: 100%;
      height: 100%;
    }
  }
`;

const Spinner = styled.div`
  ${spinner};
  /* transform: scale(1.5); */
  zoom: 1.5;
`;

const Progress = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, 20px);
  pointer-events: none;
  font-size: 12px;
  font-family: "RelativeFaux";
  opacity: 0.5;
`;

const ModularPart = React.forwardRef((props, ref) => {
  const { id, children, master } = props;
  const [partType, view, index] = id.split("_");
  const { x = 0, y = 0, w = 0, h = 0, z = 0 } = partDimensions[partType][view][index];

  const masterWidth = sizes.master;
  let masterHeight = sizes.master;

  // let masterWidth = 0;
  // let masterHeight = 0;
  if (master.current) {
    const masterRect = master.current.getBoundingClientRect();
    // masterWidth = masterRect.width;
    // masterHeight = masterRect.height;

    masterHeight *= ((100 / masterRect.width) * masterRect.height) / 100;
  }

  const mediaProps = {
    left: `${(100 / masterWidth) * x}%`,
    top: `${(100 / masterHeight) * y}%`,
    width: `${(100 / masterWidth) * w}%`,
    height: `${(100 / masterHeight) * h}%`,
    zIndex: z,
  };

  /*
  const mediaProps = {
    left: percentOfMaster(x) + '%',
    top: percentOfMaster(y) + '%',
    width: percentOfMaster(w) + '%',
    height: percentOfMaster(h) + '%',
    zIndex: z
  };
  */

  // console.log(mediaProps);

  return (
    <ModularPartWrapper id={id} ref={ref} {...mediaProps}>
      <div>{children}</div>
    </ModularPartWrapper>
  );
});

const sizes = {
  master: 3500,
  sm: 200, // 100/3500*200  = 5.7142857143
  md: 600, // 100/3500*600  = 17.1428571429
  lg: 1000, // 100/3500*1000 = 28.5714285714
};

// take with eg. front_s01_a = 825
// take widht of masterRef eg. 687
// compare that to master to get the percent: 100/3500*687 = 19.6285714286
// get width of the media we should request: 825 * (100/3500*687) ≈ 161.94px

// const mediaAffix = '/static/assets/images/tma-2-parts';
// const mediaAffix = 'https://tma-2-configurator.imgix.net';
const mediaAffix = "https://aiaiai-assets.imgix.net/tma-2-configurator";

// TODO get these from the the master sizes
// shouldn't we be able to do this with partDimensions + sizes???
const mediaSizes = {
  lg: {
    front: {
      s: {
        a: 236,
        b: 236,
      },
      h: {
        a: 707,
      },
      e: {
        a: 279,
        b: 279,
      },
      c: {
        a: 250,
      },
    },
    side: {
      s: {
        a: 364,
        b: 364,
      },
      h: {
        a: 164,
      },
      e: {
        a: 421,
      },
      c: {
        a: 79,
      },
    },
  },
};

function getMediaSuffix(uri, view, index, size = "lg") {
  const dpr = window.devicePixelRatio || 1;
  const w = mediaSizes[size][view][uri.charAt(0)][index];
  return `?w=${w}&dpr=${dpr}&auto=compress`;
}

/**
 * usage:
 * doTimeline([
 *  getAnime(earpads_front_a, 'off', 300),
 *  getAnime(earpads_front_b, 'off', 300, '-=300')
 * ])
 */
const doTimeline = (animations) => {
  const timeline = anime.timeline({ easing: "linear" });
  animations.forEach((animation) => timeline.add(...animation));
  return timeline.finished;
};

/**
 * Get an anime object based on the part and a state!
 */
const getAnime = (elem, stateName, duration, offset) => {
  // get the part state based on the name of the elem eg. "earpads_front_a"
  const stateId = elem.current.id;
  const [partType, view, index] = stateId.split("_");
  const partState = partStates[partType][view][index][stateName];

  // console.log(partState);

  const animeObj = {
    targets: elem.current,
  };
  for (const prop in partState) {
    const value = partState[prop];

    // just a number...
    animeObj[prop] = value;
  }
  // make sure 0 works too!
  // if (duration !== undefined && duration !== null && duration !== false) {
  if (typeof duration === "number") {
    animeObj.duration = duration;
  }
  // fix the offset!
  // eg. -100 = '-=100'
  if (offset < 0) {
    offset = `-=${Math.abs(offset)}`;
  }
  return [animeObj, offset];
};

const sleep = (ms) => new Promise((res) => setTimeout(() => res(), ms));

// custom "ref state" hook
function useRefState(initalState = null, cb) {
  const ref = useRef(initalState);
  const setState = (state) => {
    ref.current = state;
    if (cb) cb(ref.current);
  };
  return [ref, setState];
}

/**
 * Add's an img to a ModularPart!
 */
function addImg(elem, uri, view = "front", index = "a", cb) {
  const mediaSuffix = getMediaSuffix(uri, view, index);
  const src = `${mediaAffix}/${view}_${uri}_${index}.png${mediaSuffix}`;
  // console.log('SRC', src);
  return new Promise((resolve) => {
    const img = new Image();
    img.className = uri;
    img.onload = () => {
      if (elem.firstChild) {
        // resolve(img);
        cb();
        elem.firstChild.appendChild(img);
        resolve();
      }
    };
    img.src = src;
  });
}

/**
 * Component
 */
function Tma2(props) {
  const {
    width = "600px",
    height,
    parts = { s: [], h: [], e: [], c: [] },
    initialState = {},
    updateState = {},
    setController,
  } = props;

  const mounted = useRef(false);
  const master = useRef();
  const spinnerRef = useRef();
  // const view = useRef('front');
  // const size = useRef('lg');

  const [s, setS] = useRefState(initialState.s || "s02", updateState.setS);
  const [h, setH] = useRefState(initialState.h || "h02", updateState.setH);
  const [e, setE] = useRefState(initialState.e || "e02", updateState.setE);
  const [c, setC] = useRefState(initialState.c || "no-cable", updateState.setC);
  const [view, setView] = useRefState(initialState.view || "front", updateState.view);
  const [working, setWorking] = useRefState(
    updateState.working,
    updateState.setWorking
  );
  // const [loadingProgress, setLoadingProgress] = useRefState([0,0]);

  const imageCount = useRef(0);
  const imageLoadProgress = useRef(0);
  const progressRef = useRef();
  function updateProgress() {
    // console.log(`${imageCount.current}/${imageLoadProgress.current}`);
    if (progressRef.current) {
      if (imageCount.current > 0) {
        // progressRef.current.innerHTML = `${imageLoadProgress.current}/${imageCount.current}`;
        progressRef.current.innerHTML = `${Math.round(
          (100 / imageCount.current) * imageLoadProgress.current
        )}%`;
        if (imageLoadProgress.current === imageCount.current) {
          progressRef.current.style.display = "none";
        }
      }
    }
  }

  /**
   * Refs for parts!
   */

  // front refs
  const speakers_front_a = useRef();
  const speakers_front_b = useRef();
  const earpads_front_a = useRef();
  const earpads_front_b = useRef();
  const headband_front_a = useRef();
  const cable_front_a = useRef();
  // side refs
  /* const speakers_side_a = useRef();
  const speakers_side_b = useRef();
  const earpads_side_a = useRef();
  const headband_side_a = useRef();
  const cable_side_a = useRef(); */

  /**
   * Animation functions
   */
  const changeViewAnims = {
    front: {
      in: () =>
        doTimeline([
          // headbadn in
          getAnime(headband_front_a, "on", 300),
          // speaker units in
          getAnime(speakers_front_a, "on", 200, -100),
          getAnime(speakers_front_b, "on", 200, -100),
          // earpads in
          getAnime(earpads_front_a, "on", 200, -100),
          getAnime(earpads_front_b, "on", 200, -100),
          // cable in
          getAnime(cable_front_a, "on", 200, -100),
        ]),
      out: () =>
        doTimeline([
          // cable out
          getAnime(cable_front_a, "out", 200),
          // earpads out
          getAnime(earpads_front_a, "out", 200, -100),
          getAnime(earpads_front_b, "out", 200, -100),
          // speaker units out
          getAnime(speakers_front_a, "out", 200, -100),
          getAnime(speakers_front_b, "out", 200, -100),
          // headbadn out
          getAnime(headband_front_a, "out", 300, -150),
        ]),
      outInstant: () =>
        doTimeline([
          // cable out
          getAnime(cable_front_a, "out", 0),
          // earpads out
          getAnime(earpads_front_a, "out", 0),
          getAnime(earpads_front_b, "out", 0),
          // speaker units out
          getAnime(speakers_front_a, "out", 0),
          getAnime(speakers_front_b, "out", 0),
          // headbadn out
          getAnime(headband_front_a, "out", 0),
        ]),
    } /* ,
    side: {
      in: () =>
        doTimeline([
          // headbadn in
          getAnime(headband_side_a, 'on', 300),
          // speaker units in
          getAnime(speakers_side_a, 'on', 400, -200),
          getAnime(speakers_side_b, 'on', 400, -400),
          // earpads in
          getAnime(earpads_side_a, 'on', 400, -100),
          // cable in
          getAnime(cable_side_a, 'on', 400, -200)
        ]),
      out: () =>
        doTimeline([
          // cable out
          getAnime(cable_side_a, 'out', 400),
          // earpads out
          getAnime(earpads_side_a, 'out', 400, -300),
          // speaker units out
          getAnime(speakers_side_a, 'out', 400, -200),
          getAnime(speakers_side_b, 'out', 400, -400),
          // headbadn out
          getAnime(headband_side_a, 'out', 300, -200)
        ]),
      outInstant: () =>
        doTimeline([
          // cable out
          getAnime(cable_side_a, 'out', 0),
          // earpads out
          getAnime(earpads_side_a, 'out', 0),
          // speaker units out
          getAnime(speakers_side_a, 'out', 0),
          getAnime(speakers_side_b, 'out', 0),
          // headbadn out
          getAnime(headband_side_a, 'out', 0)
        ])
    } */,
  };

  const changeSpeakersAnims = {
    front: {
      in: () =>
        doTimeline([
          // speakers in
          getAnime(speakers_front_a, "on", 300),
          getAnime(speakers_front_b, "on", 300, -100),
          // cable in
          getAnime(cable_front_a, "on", 300),
          // earpads in
          getAnime(earpads_front_a, "on", 100),
          getAnime(earpads_front_b, "on", 100, -100),
        ]),
      out: () =>
        doTimeline([
          // earpads up
          getAnime(earpads_front_a, "off", 100),
          getAnime(earpads_front_b, "off", 100, -100),
          // cable out
          getAnime(cable_front_a, "out", 300),
          // speakers out
          getAnime(speakers_front_a, "out", 300, -150),
          getAnime(speakers_front_b, "out", 300, -100),
        ]),
    } /* ,
    side: {
      in: () =>
        doTimeline([
          // speakers in
          getAnime(speakers_side_a, 'on', 400),
          getAnime(speakers_side_b, 'on', 400, -400),
          // cable in
          getAnime(cable_side_a, 'on', 400, -400),
          // earpads in
          getAnime(earpads_side_a, 'on', 150)
        ]),
      out: () =>
        doTimeline([
          // earpads up
          getAnime(earpads_side_a, 'off', 150),
          // cable out
          getAnime(cable_side_a, 'out', 400),
          // speakers out
          getAnime(speakers_side_a, 'out', 400, -400),
          getAnime(speakers_side_b, 'out', 400, -400)
        ])
    } */,
  };

  const changeHeadbandAnims = {
    front: {
      in: () =>
        doTimeline([
          getAnime(headband_front_a, "on", 200),
          getAnime(earpads_front_a, "on", 200, -100),
          getAnime(earpads_front_b, "on", 200, -200),
          getAnime(cable_front_a, "on", 200, -200),
          getAnime(speakers_front_a, "on", 200, -200),
          getAnime(speakers_front_b, "on", 200, -200),
        ]),
      out: () =>
        doTimeline([
          getAnime(speakers_front_a, "off", 200),
          getAnime(speakers_front_b, "off", 200, -200),
          getAnime(earpads_front_a, "off2", 200, -200),
          getAnime(earpads_front_b, "off2", 200, -200),
          getAnime(cable_front_a, "off", 200, -200),
          getAnime(headband_front_a, "off", 200, -100),
        ]),
    } /* ,
    side: {
      in: () =>
        doTimeline([
          getAnime(headband_side_a, 'on', 200),
          getAnime(earpads_side_a, 'on', 200, -100),
          getAnime(cable_side_a, 'on', 200, -200),
          getAnime(speakers_side_a, 'on', 200, -200),
          getAnime(speakers_side_b, 'on', 200, -200)
        ]),
      out: () =>
        doTimeline([
          getAnime(speakers_side_a, 'off', 200),
          getAnime(speakers_side_b, 'off', 200, -200),
          getAnime(earpads_side_a, 'off2', 200, -200),
          getAnime(cable_side_a, 'off', 200, -200),
          getAnime(headband_side_a, 'off', 200, -100)
        ])
    } */,
  };

  const changeEarpadsAnims = {
    front: {
      in: () =>
        doTimeline([
          getAnime(earpads_front_b, "off", 300),
          getAnime(earpads_front_b, "on", 100),
          getAnime(earpads_front_a, "off", 300),
          getAnime(earpads_front_a, "on", 100),
        ]),
      out: () =>
        doTimeline([
          getAnime(earpads_front_a, "off", 100),
          getAnime(earpads_front_a, "out", 300),
          getAnime(earpads_front_b, "off", 100, -100),
          getAnime(earpads_front_b, "out", 300),
        ]),
    } /* ,
    side: {
      in: () =>
        doTimeline([
          getAnime(earpads_side_a, 'off', 300),
          getAnime(earpads_side_a, 'on', 150)
        ]),
      out: () =>
        doTimeline([
          getAnime(earpads_side_a, 'off', 150),
          getAnime(earpads_side_a, 'out', 300)
        ])
    } */,
  };

  const changeCableAnims = {
    front: {
      in: () => doTimeline([getAnime(cable_front_a, "on", 300)]),
      out: () => doTimeline([getAnime(cable_front_a, "out", 300)]),
    } /* ,
    side: {
      in: () => doTimeline([getAnime(cable_side_a, 'on', 400)]),
      out: () => doTimeline([getAnime(cable_side_a, 'out', 400)])
    } */,
  };

  /**
   * Controller functions
   */
  const changeSpeakers = async (uri) => {
    // already have this part selected...
    if (s.current === uri) return;
    if (working.current) return;
    setS(uri);
    await sleep(100);
    setWorking(true);
    await sleep(100);

    await changeSpeakersAnims[view.current].out();
    s.current = uri; // we dont want to truigger update in parent!
    setImages();
    await changeSpeakersAnims[view.current].in();
    // await sleep(200);
    setWorking(false);
  };

  const changeHeadband = async (uri) => {
    // already have this part selected...
    if (h.current === uri) return;
    if (working.current) return;
    setH(uri);
    await sleep(50);
    setWorking(true);
    await sleep(100);

    await changeHeadbandAnims[view.current].out();
    h.current = uri; // we dont want to truigger update in parent!
    setImages();
    await changeHeadbandAnims[view.current].in();
    await sleep(100);
    setWorking(false);
  };

  const changeEarpads = async (uri) => {
    // already have this part selected...
    if (e.current === uri) return;
    if (working.current) return;
    setE(uri);
    await sleep(50);
    setWorking(true);
    await sleep(100);

    await changeEarpadsAnims[view.current].out();
    e.current = uri; // we dont want to truigger update in parent!
    setImages();
    await changeEarpadsAnims[view.current].in();
    await sleep(100);
    setWorking(false);
  };

  const changeCable = async (uri) => {
    // already have this part selected...
    if (c.current === uri) return;
    if (working.current) return;
    setC(uri);
    await sleep(50);
    setWorking(true);
    await sleep(100);

    await changeCableAnims[view.current].out();
    c.current = uri; // we dont want to truigger update in parent!
    setImages();
    await changeCableAnims[view.current].in();
    await sleep(100);
    setWorking(false);
  };

  const toggleView = async () => {
    if (view.current === "front") {
      await changeView("side");
    } else {
      await changeView("front");
    }
    setWorking(false);
  };

  const changeView = async (toView = "front") => {
    if (toView === view.current) return;
    if (working.current) return;
    setWorking(true);
    // we are already in this view...
    await changeViewAnims[view.current].out();
    await changeViewAnims[toView].in();
    view.current = toView;
    setWorking(false);
  };

  /**
   * Updates the visible part images
   * And layer order for on/over earpads
   */
  function setImages() {
    try {
      // speakers_front_a
      speakers_front_a.current
        .querySelectorAll("img")
        .forEach((elem) => (elem.style.opacity = 0));
      speakers_front_a.current.querySelector(`.${s.current}`).style.opacity = 1;

      // speakers_front_b
      speakers_front_b.current
        .querySelectorAll("img")
        .forEach((elem) => (elem.style.opacity = 0));
      speakers_front_b.current.querySelector(`.${s.current}`).style.opacity = 1;

      // speakers_side_a
      /* speakers_side_a.current
        .querySelectorAll('img')
        .forEach(elem => (elem.style.opacity = 0));
      speakers_side_a.current.querySelector(`.${s.current}`).style.opacity = 1; */

      // speakers_side_b
      /* speakers_side_b.current
        .querySelectorAll('img')
        .forEach(elem => (elem.style.opacity = 0));
      speakers_side_b.current.querySelector(`.${s.current}`).style.opacity = 1; */

      // headband_front_a
      headband_front_a.current
        .querySelectorAll("img")
        .forEach((elem) => (elem.style.opacity = 0));
      headband_front_a.current.querySelector(`.${h.current}`).style.opacity = 1;

      // headband_side_a
      /* headband_side_a.current
        .querySelectorAll('img')
        .forEach(elem => (elem.style.opacity = 0));
      headband_side_a.current.querySelector(`.${h.current}`).style.opacity = 1; */

      const overEarpads = ["e04", "e05", "e06", "e08"];
      const earpads_a_index = overEarpads.includes(e.current) ? 7 : 2;
      const earpads_b_index = overEarpads.includes(e.current) ? 8 : 3;

      // earpads_front_a
      earpads_front_a.current
        .querySelectorAll("img")
        .forEach((elem) => (elem.style.opacity = 0));
      earpads_front_a.current.querySelector(`.${e.current}`).style.opacity = 1;
      earpads_front_a.current.style.zIndex = earpads_a_index;

      // earpads_front_b
      earpads_front_b.current
        .querySelectorAll("img")
        .forEach((elem) => (elem.style.opacity = 0));
      earpads_front_b.current.querySelector(`.${e.current}`).style.opacity = 1;
      earpads_front_b.current.style.zIndex = earpads_b_index;

      // earpads_side_a
      /* earpads_side_a.current
        .querySelectorAll('img')
        .forEach(elem => (elem.style.opacity = 0));
      earpads_side_a.current.querySelector(`.${e.current}`).style.opacity = 1; */

      // cable_front_a
      cable_front_a.current
        .querySelectorAll("img")
        .forEach((elem) => (elem.style.opacity = 0));
      if (c.current !== "no-cable") {
        cable_front_a.current.querySelector(`.${c.current}`).style.opacity = 1;
      }

      // cable_side_a
      /* cable_side_a.current
        .querySelectorAll('img')
        .forEach(elem => (elem.style.opacity = 0));
      cable_side_a.current.querySelector(`.${c.current}`).style.opacity = 1; */
    } catch (e) {
      console.log("Tma2 missing parts");
    }
  }

  /**
   * Preloads all the images we need!
   */
  async function preloadImages(onProgress = (n, t) => {}) {
    // console.log('parts.s', parts.s);
    const loadParts = [
      // speakers_front_a
      ...parts.s.map((part) =>
        addImg(speakers_front_a.current, part.key, "front", "a", progressCallback)
      ),
      // speakers_front_b
      ...parts.s.map((part) =>
        addImg(speakers_front_b.current, part.key, "front", "b", progressCallback)
      ),
      // speakers_side_a
      /* ...parts.s.map(part =>
        addImg(speakers_side_a.current, part.key, 'side', 'a', progressCallback)
      ), */
      // speakers_side_b
      /* ...parts.s.map(part =>
        addImg(speakers_side_b.current, part.key, 'side', 'b', progressCallback)
      ), */
      // headband_front_a
      ...parts.h.map((part) =>
        addImg(headband_front_a.current, part.key, "front", "a", progressCallback)
      ),
      // headband_side_a
      /* ...parts.h.map(part =>
        addImg(headband_side_a.current, part.key, 'side', 'a', progressCallback)
      ), */
      // earpads_front_a
      ...parts.e.map((part) =>
        addImg(earpads_front_a.current, part.key, "front", "a", progressCallback)
      ),
      // earpads_front_b
      ...parts.e.map((part) =>
        addImg(earpads_front_b.current, part.key, "front", "b", progressCallback)
      ),
      // earpads_side_a
      /* ...parts.e.map(part =>
        addImg(earpads_side_a.current, part.key, 'side', 'a', progressCallback)
      ), */
      // cable_front_a
      ...parts.c.map((part) =>
        addImg(cable_front_a.current, part.key, "front", "a", progressCallback)
      ),
      // cable_side_a
      /* ...parts.c.map(part =>
        addImg(cable_side_a.current, part.key, 'side', 'a', progressCallback)
      ) */
    ];

    imageCount.current = loadParts.length;

    function progressCallback(d) {
      imageLoadProgress.current += 1;
      updateProgress();
    }

    await Promise.all(loadParts);

    setImages();
  }

  /**
   * Define the controller, exposed to the parent component that should control the TMA2
   */
  const controller = {
    changeSpeakers,
    changeHeadband,
    changeEarpads,
    changeCable,
    toggleView,
    changeView,
    start: () => {
      changeViewAnims.front.in();
    },
  };

  /**
   * When mounting the component, we set all the stuff up...
   */
  async function onMount() {
    // console.log('MOUNT');
    // if (mounted.current) return;
    // mounted.current = true;
    setWorking(true);
    // hide both views
    changeViewAnims.front.outInstant();
    // changeViewAnims['side'].outInstant();
    // setImages();
    await preloadImages();
    if (spinnerRef.current) {
      spinnerRef.current.style.display = "none";
    }
    if (setController) setController(controller);
    // make sure state is up to date after mount
    if (updateState.setS) updateState.setS(s.current);
    if (updateState.setH) updateState.setH(h.current);
    if (updateState.setE) updateState.setE(e.current);
    if (updateState.setC) updateState.setC(c.current);
    if (updateState.setWorking) updateState.setWorking(working.current);
    if (updateState.setView) updateState.setView(view.current);
    setWorking(false);
  }
  useEffect(() => {
    if (master.current && !mounted.current) {
      mounted.current = true;
      onMount();
    }
  }, [master]);

  // console.log('🔥 RENDER');
  // master will change after first render, and first then we will mount and ModularPart will render!

  return (
    <Square width={width} height={height} ref={master}>
      <Spinner ref={spinnerRef} />
      <Progress ref={progressRef} />
      <ModularPart id="headband_front_a" ref={headband_front_a} master={master} />

      <ModularPart id="earpads_front_a" ref={earpads_front_a} master={master} />
      <ModularPart id="earpads_front_b" ref={earpads_front_b} master={master} />
      <ModularPart id="cable_front_a" ref={cable_front_a} master={master} />
      <ModularPart id="speakers_front_a" ref={speakers_front_a} master={master} />
      <ModularPart id="speakers_front_b" ref={speakers_front_b} master={master} />

      {/*
      <ModularPart id="headband_side_a" ref={headband_side_a} master={master} />
      <ModularPart id="earpads_side_a" ref={earpads_side_a} master={master} />
      <ModularPart id="cable_side_a" ref={cable_side_a} master={master} />
      <ModularPart id="speakers_side_a" ref={speakers_side_a} master={master} />
      <ModularPart id="speakers_side_b" ref={speakers_side_b} master={master} />
    */}
    </Square>
  );
}

export default Tma2;
